<script context="module" lang="ts">
  import { linkPagePath } from '$lib/utils/routes';
  import type { LinkPage, NavItem } from '$lib/sanity/schemas';

  export const resolveHref = (item: NavItem) => {
    if (item.link?.destination.type === 'url') {
      return item.link.destination.href;
    } else if (item.link?.destination.type === 'page') {
      return linkPagePath(item.link.destination.page as LinkPage);
    }
  };

  export const getDropdownItemsByParent = (navItems: NavItem[]) => {
    return navItems.reduce((acc: Record<string, NavItem[]>, item: NavItem) => {
      if (item.parent && acc[item.parent]) {
        acc[item.parent] = [...acc[item.parent], item];
      } else if (item.parent) {
        acc[item.parent] = [item];
      }
      return acc;
    }, {});
  };
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import { browser } from '$app/environment';
  import { cx } from '@emotion/css';
  import { clickOutside } from 'svelte-use-click-outside';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import { modals } from '$lib/stores/modals';
  import { TwistStack, ResponsiveVars } from '@twist/core';
  import NavigationLogo from '$components/Pages/Navigation/NavigationBar/NavigationLogo.svelte';
  import CloseX from '$components/Icons/CloseX.svelte';
  import Menu from '$components/Icons/Menu.svelte';
  import MobileNavItems from './MobileNavItems.svelte';
  import DesktopNavItems from './DesktopNavItems.svelte';

  export let mobileNavOpen = false;
  export let expandSpeedInPx = 1200;
  export let handleCloseClicked = () => {};
  export let hasActiveItem = false;
  export let activeItem: NavItem | undefined = undefined;
  export let closeDropdownAtScrollY: number | undefined = undefined;

  let scrollY = 0;
  let navbarHeight = 74;
  let dropdownHeight = 0;
  let button: HTMLButtonElement;
  const landingShowLogoAtScrollY = 375;
  let showLogoOnLanding = false;

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);
  $: isCareersPage = $page.url.pathname === '/careers/jobs';
  $: isLandingPage = $page.url.pathname === '/';
  $: dropdownDuration = dropdownHeight / expandSpeedInPx;
  $: hasActiveItem = !!activeItem;
  $: showLogoOnLanding =
    isLandingPage && (scrollY > landingShowLogoAtScrollY || $modals.has('mobileNavModal'));

  $: if (closeDropdownAtScrollY && scrollY > closeDropdownAtScrollY) {
    activeItem = undefined;
  }

  const isTouchDevice = browser && matchMedia('(hover: none) and (pointer: coarse)').matches;

  const navItems = $page.data.navigation?.primaryNavigation?.navItems || ([] as NavItem[]);

  const handleDropdown = (item: NavItem) => {
    if (activeItem?.id === item.id) {
      activeItem = undefined;
    } else {
      activeItem = item;
    }
  };

  const closeDropdown = () => {
    activeItem = undefined;
  };

  const toggleMobile = () => {
    modals.toggle('mobileNavModal', button);
    handleCloseClicked();
  };

  const responsiveVars = new ResponsiveVars()
    .addFluid('nav-item-font-size', toFluidVarInput(16, 14))
    .addFluid('nav-item-font-size-reskin', toFluidVarInput(12, 12))
    .addFluid('nav-item-inner-gap', toFluidVarInput(3, 3))
    .addFluid('nav-item-inner-gap-reskin', toFluidVarInput(9, 9))
    .addFluid('nav-item-y', toFluidVarInput(4, 5))
    .addFluid('nav-item-y-reskin', toFluidVarInput(15, 9))
    .addFluid('dropdown-nav-item-y-mobile-reskin', toFluidVarInput(9, 0))
    .addFluid('nav-item-x', toFluidVarInput(8, 12))
    .addFluid('navbar-py', toFluidVarInput(15, 15))
    .addFluid('dropdown-icon-size', toFluidVarInput(14, 14))
    .addFluid('mobile-icon-size', toFluidVarInput(24, 24))
    .addFluid('mobile-toggle-padding', toFluidVarInput(6, 6))
    .addFluid('logo-left', toFluidVarInput(0, 8))
    .addFluid('logo-right', toFluidVarInput(0, 30))
    .addFluid('checkmark-ml', toFluidVarInput(6, 8))
    .addFluid('checkmark-size', toFluidVarInput(12, 12))
    .addFluid('dropdown-item-wrapper-pb', toFluidVarInput(15, 9))
    .addFluid('mobile-dropdown-item-pl', toFluidVarInput(36, 0))
    .toCSS();
</script>

<svelte:window bind:scrollY />

<nav
  data-testid="global-nav"
  class={cx('navigation', responsiveVars, {
    isTouchDevice,
    'navigation--mobile': mobileNavOpen,
    'navigation--sticky': !isCareersPage,
    reskin: brandReskinEnabled
  })}
  use:clickOutside={closeDropdown}
  aria-label="Main navigation"
  style={`--height: ${activeItem ? navbarHeight + dropdownHeight : navbarHeight}px; --dropdown-duration: ${dropdownDuration}s;`}
>
  <div class="container">
    <div class="navigation-bar" bind:clientHeight={navbarHeight}>
      <TwistStack direction="row" align="center" justify="flex-start" wrap="nowrap">
        <span
          class={cx('logo-icon', {
            'logo-icon-reskin': brandReskinEnabled,
            landing: isLandingPage,
            showLanding: showLogoOnLanding
          })}
        >
          <NavigationLogo />
        </span>
        <DesktopNavItems
          {navItems}
          {activeItem}
          {handleDropdown}
          {navbarHeight}
          {mobileNavOpen}
          bind:dropdownHeight
        />
      </TwistStack>
      <TwistStack
        direction="row"
        align="center"
        justify="flex-end"
        spacing={toFluidVarInput(12, 0)}
      >
        <button
          bind:this={button}
          class="mobile-toggle"
          on:click={toggleMobile}
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          data-testid="mobile-nav-modal-toggle"
        >
          <div class="mobile-icon">
            {#if mobileNavOpen}
              <CloseX />
            {:else}
              <Menu />
            {/if}
          </div>
        </button>
      </TwistStack>
    </div>

    {#if mobileNavOpen}
      <MobileNavItems {navItems} {activeItem} {handleDropdown} {handleCloseClicked} />
    {/if}
  </div>
</nav>

<style lang="scss">
  @import 'src/scss/variables';
  @import 'src/scss/media';

  .navigation {
    position: absolute;
    overflow-y: auto;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(34px);
    margin-top: 0;
    z-index: map-get($zIndex, primary-nav);

    @include at(lg) {
      height: var(--height);
      transition: height var(--dropdown-duration) var(--twist-ease-out-1);
    }
  }

  .navigation.reskin {
    background-color: $background-depth-0-color;
    border-bottom: var(--twist-color-gray-15) 1px solid;
    backdrop-filter: unset;

    @include at(lg) {
      // I suspect bind:clientHeight rounds down. Along with the border-bottom, this slight discrepancy
      // in actual element height vs. bind:clientHeight value causes a scrollbar on desktop.
      // This overflow-y: hidden prevents that.
      overflow-y: hidden;
    }
  }

  .navigation--sticky {
    position: fixed;
  }

  .navigation.navigation--mobile {
    height: 100vh;
  }

  .navigation-bar {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--navbar-py) 0;
  }

  :global(.navigation .nav-item) {
    width: max-content;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--nav-item-inner-gap);
    border: none;
    border-radius: 1000px;
    padding: var(--nav-item-y) var(--nav-item-x);
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--nav-item-font-size);
    font-weight: 600;
    letter-spacing: 0.96px;
    background: none;
    outline: none;
    cursor: pointer;
    text-decoration: none;
    transition:
      background var(--twist-duration-200) ease,
      color var(--twist-duration-200) ease;

    &:active {
      opacity: 0.6;
    }
  }

  :global(.navigation.reskin .nav-item) {
    gap: var(--nav-item-inner-gap-reskin);
    padding: var(--nav-item-y-reskin) 0;
    font-size: var(--nav-item-font-size-reskin);
    font-weight: 400;
    text-transform: uppercase;
  }

  :global(.navigation.reskin .nav-item__text) {
    letter-spacing: 0.96px;
  }

  :global(.nav-item--mobile-root .nav-item) {
    width: 100%;
    margin: var(--mobile-nav-item-py) 0;
  }

  :global(.navigation.reskin .nav-item--mobile-root .nav-item) {
    width: unset;
    margin: 0;
  }

  :global(.navigation .nav-item.current) {
    font-weight: 700;
  }

  :global(.navigation.reskin .nav-item.current) {
    font-weight: unset;
  }

  :global(.navigation .nav-item .checkmark) {
    display: flex;
    align-self: center;
    height: var(--checkmark-size);
    width: var(--checkmark-size);
    margin-left: var(--checkmark-ml);
  }

  :global(.navigation.reskin .nav-item .checkmark) {
    margin-left: 0;
  }

  :global(.navigation:not(.isTouchDevice) .nav-item) {
    &:hover,
    &:focus-visible {
      outline: 1px solid var(--twist-color-gray-100);
    }
  }

  :global(.navigation.reskin:not(.isTouchDevice) .nav-item) {
    &:hover,
    &:focus-visible {
      outline: none;
      font-weight: 600;
    }
  }

  :global(.navigation.reskin:not(.isTouchDevice) .nav-item__text) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    // prevents layout shift on hover due to font weight increase to semibold
    &::after {
      content: attr(data-text) / '';
      height: 0;
      visibility: hidden;
      overflow: hidden;
      user-select: none;
      pointer-events: none;
      font-weight: 600;

      @media speech {
        display: none;
      }
    }
  }

  :global(.navigation ul) {
    padding: 0;
    margin: 0;
  }

  :global(.navigation li) {
    list-style: none;
  }

  :global(.navigation .dropdown-item-wrapper) {
    padding-bottom: var(--dropdown-item-wrapper-pb);
    padding-left: 40px;

    @include at(lg) {
      padding-left: 0;

      &:not(.last) {
        border-bottom: 1px solid rgba(0, 0, 0, 0.3);
      }
    }
  }

  :global(.navigation.reskin .dropdown-item-wrapper) {
    padding-left: var(--mobile-dropdown-item-pl);

    @include at(lg) {
      &:not(.last) {
        border-bottom: 1px solid $border-depth-2-color;
      }
    }
  }

  :global(.navigation .dropdown-item-wrapper .nav-item) {
    margin-left: calc(-1 * var(--nav-item-x));
  }

  :global(.navigation.reskin .dropdown-item-wrapper .nav-item) {
    margin-left: 0;
  }

  :global(.navigation--mobile.reskin .dropdown-item-wrapper .nav-item) {
    padding: var(--dropdown-nav-item-y-mobile-reskin) 0;
  }

  :global(.navigation .dropdown-icon) {
    display: flex;
    align-self: center;
    height: var(--dropdown-icon-size);
    width: var(--dropdown-icon-size);
    transition: transform var(--twist-ease-out-1) var(--twist-duration-150);

    @include at(lg) {
      align-self: flex-end;
    }
  }

  :global(.navigation .dropdown-icon--active) {
    transform: rotate(180deg);
  }

  .logo-icon {
    margin-left: calc(-1 * var(--logo-left));
    margin-right: var(--logo-right);
  }

  .logo-icon-reskin {
    margin-left: 0;
    margin-right: var(--logo-right);
    position: relative;
    pointer-events: all;
  }
  .logo-icon.landing {
    // TODO: replace fade with proper logo build animation with delays on each cell
    will-change: opacity;
    opacity: 0;
    transition: opacity 0.2s ease-in;

    &.showLanding {
      opacity: 1;
    }
  }

  .mobile-toggle {
    padding: var(--mobile-toggle-padding);
    border: none;
    border-radius: 1000px;
    color: var(--twist-color-gray-100);
    background: none;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      outline: 1px solid var(--twist-color-gray-100);
    }

    &:active {
      opacity: 0.6;
    }

    @include at(lg) {
      display: none;
    }
  }

  .mobile-icon {
    height: var(--mobile-icon-size);
    width: var(--mobile-icon-size);
  }
</style>
